import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle,
  Lock,
  LockKey,
  CurrencyInr,
  WhatsappLogo,
  Copy,
  ArrowRight,
  FilePdf,
  VideoCamera,
  Sparkle,
  ShieldCheck,
  House,
} from "@phosphor-icons/react";
import Logo from "../components/Logo";
import RazorpayButton from "../components/RazorpayButton";
import { communityRegister, communityPayment } from "../lib/api";
import { toast } from "sonner";
import {
  MarketProfileEducation,
  HowToGetStarted,
  InstructionsForNewMembers,
  DailyLevelsSample,
  CommunityFAQ,
  CommunityFooter,
} from "../components/CommunitySections";

const EXP_OPTIONS = ["Beginner", "1–3 yrs", "3–5 yrs", "5+ yrs"];

const STEPS = [
  { n: 1, title: "Your Details" },
  { n: 2, title: "Pay ₹999" },
  { n: 3, title: "Join Group" },
];

function StepIndicator({ current }) {
  return (
    <ol className="flex items-center gap-3" data-testid="step-indicator">
      {STEPS.map((s, i) => {
        const done = current > s.n;
        const active = current === s.n;
        return (
          <li key={s.n} className="flex items-center gap-3">
            <div
              className={`w-9 h-9 flex items-center justify-center border font-mono-acc font-bold text-sm ${
                active
                  ? "bg-[#D9381E] text-white border-[#D9381E]"
                  : done
                    ? "bg-[#0A0A0A] text-[#F4F3ED] border-[#0A0A0A]"
                    : "bg-[#F4F3ED] text-[#A1A1AA] border-[#D4D0C5]"
              }`}
            >
              {done ? <CheckCircle size={18} weight="fill" /> : s.n}
            </div>
            <div className="hidden sm:block">
              <div className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]">
                Step {s.n}
              </div>
              <div
                className={`font-display font-bold text-sm tracking-tight ${
                  active ? "text-[#D9381E]" : done ? "text-[#0A0A0A]" : "text-[#A1A1AA]"
                }`}
              >
                {s.title}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <span className="hidden md:inline-block w-10 h-px bg-[#D4D0C5] mx-1" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function Community() {
  const [step, setStep] = useState(1);
  const formRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    experience: "Beginner",
  });
  const [signupId, setSignupId] = useState("");
  const [payRef, setPayRef] = useState("");
  const [whatsappLink, setWhatsappLink] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submitStep1 = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.location.trim()) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await communityRegister({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        location: form.location.trim(),
        experience: form.experience,
      });
      setSignupId(res.signup_id);
      setStep(2);
      toast.success("Saved! Pay ₹999 to unlock the group.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    const ref = payRef.trim();
    if (!ref) {
      toast.error("Enter the Razorpay UTR / payment reference.");
      return;
    }
    // Client-side preflight pattern check (server enforces too)
    const looksLegit =
      /^pay_[A-Za-z0-9]{10,30}$/.test(ref) ||
      /^order_[A-Za-z0-9]{10,30}$/.test(ref) ||
      /^[0-9]{12,22}$/.test(ref) ||
      /^[A-Z0-9]{14,30}$/.test(ref);
    if (!looksLegit) {
      toast.error(
        "Paste a real Razorpay Payment ID (starts with pay_) or 12-22 digit UTR."
      );
      return;
    }
    setLoading(true);
    try {
      const res = await communityPayment({
        signup_id: signupId,
        payment_ref: ref,
      });
      // Unique + valid ref → backend instantly returns whatsapp_link.
      if (res.whatsapp_link) {
        setWhatsappLink(res.whatsapp_link);
        setStep(3);
        toast.success("Payment confirmed. WhatsApp group unlocked!");
      } else {
        toast.error("Unexpected response. Please WhatsApp us at +91 81293 87138.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record payment.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (!whatsappLink) return;
    navigator.clipboard?.writeText(whatsappLink);
    toast.success("Link copied");
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#F4F3ED] text-[#0A0A0A] font-body">
      {/* Header */}
      <header className="border-b border-[#D4D0C5] sticky top-0 z-30 bg-[#F4F3ED]/85 backdrop-blur-xl">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="community-home-link">
            <Logo className="h-10 w-auto" />
            <div className="leading-tight hidden sm:block">
              <div className="font-display font-black text-sm tracking-tight">
                GREAT EAGLE TECH
              </div>
              <div className="font-mono-acc text-[9px] tracking-[0.25em] text-[#D9381E]">
                COMMUNITY · ₹999
              </div>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="back-to-main"
            className="inline-flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.25em] uppercase border border-[#0A0A0A] px-3 py-2 hover:bg-[#0A0A0A] hover:text-[#F4F3ED] transition-colors"
          >
            <House size={14} /> Main site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-5 lg:px-10 pt-12 lg:pt-16 pb-8">
        <div className="max-w-[1100px] mx-auto">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-4">
            JOIN THE COMMUNITY
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.9]">
            ₹999 · 10 trading days
            <br />
            of <span className="text-[#D9381E]">live trading.</span>
          </h1>
          <p className="font-body text-base lg:text-lg text-[#4A4A4A] mt-6 max-w-2xl">
            <strong>₹99 / trading day.</strong> Get introduced to our ecosystem
            — 10 trading days of live support inside our private WhatsApp group
            with Mr. Vishnu Gupthan R. You'll receive our{" "}
            <strong>daily trade plan</strong>, <strong>Nifty / Bank Nifty /
            Midcap futures levels</strong>, <strong>range updates on
            WhatsApp</strong>, plus join our <strong>EOD discussion</strong>{" "}
            sessions live. Straight MP & OF style: fewer trades, higher
            conviction.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
            {[
              { icon: VideoCamera, text: "EOD discussion · attend live" },
              { icon: CurrencyInr, text: "₹999 · 10 trading days" },
              { icon: Sparkle, text: "Daily trade plan on WhatsApp" },
              { icon: FilePdf, text: "Futures levels + ranges" },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  className="flex items-start gap-2.5 border border-[#0A0A0A] bg-[#F4F3ED] p-3"
                >
                  <Icon size={20} weight="duotone" className="text-[#D9381E] shrink-0" />
                  <span className="font-body text-xs leading-snug">{p.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Step indicator */}
      <section className="px-5 lg:px-10" ref={formRef} id="register">
        <div className="max-w-[1100px] mx-auto border-y border-[#D4D0C5] py-5">
          <StepIndicator current={step} />
        </div>
      </section>

      {/* Step content */}
      <section className="px-5 lg:px-10 py-10 lg:py-14">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* LEFT — main panel */}
          <div className="lg:col-span-7">
            {step === 1 && (
              <div
                data-testid="step-1-panel"
                className="border border-[#0A0A0A] bg-[#F4F3ED] p-7 lg:p-10"
              >
                <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
                  Step 1 · Your Details
                </div>
                <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-2">
                  Tell us about you.
                </h2>
                <p className="font-body text-sm text-[#4A4A4A] mb-6">
                  We'll save your details, then unlock the payment step.
                </p>

                <form onSubmit={submitStep1} className="space-y-4" data-testid="community-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                        Name *
                      </label>
                      <input
                        data-testid="community-name"
                        required
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        placeholder="e.g. Arjun K."
                        className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
                      />
                    </div>
                    <div>
                      <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                        Phone *
                      </label>
                      <input
                        data-testid="community-phone"
                        required
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="+91 ••••• •••••"
                        className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Email *
                    </label>
                    <input
                      data-testid="community-email"
                      required
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="you@email.com"
                      className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
                    />
                    <p className="font-mono-acc text-[10px] tracking-[0.15em] text-[#4A4A4A] mt-1.5">
                      We'll message you here with trade plans, futures levels &amp; range updates.
                    </p>
                  </div>
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Location (City, State) *
                    </label>
                    <input
                      data-testid="community-location"
                      required
                      name="location"
                      value={form.location}
                      onChange={onChange}
                      placeholder="e.g. Bengaluru, KA"
                      className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
                    />
                  </div>
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Trading Experience
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {EXP_OPTIONS.map((o) => (
                        <button
                          key={o}
                          type="button"
                          data-testid={`community-exp-${o}`}
                          onClick={() => setForm((p) => ({ ...p, experience: o }))}
                          className={`px-3 py-2.5 border text-xs font-body tracking-[0.15em] uppercase transition-colors ${
                            form.experience === o
                              ? "bg-[#0A0A0A] text-[#F4F3ED] border-[#0A0A0A]"
                              : "bg-[#F4F3ED] text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#EAE8DF]"
                          }`}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    data-testid="community-submit-1"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#0A0A0A] text-[#F4F3ED] py-4 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3"
                  >
                    {loading ? "Saving…" : "Save & continue to payment"}
                    {!loading && <ArrowRight weight="bold" size={16} />}
                  </button>
                </form>
              </div>
            )}

            {step === 2 && (
              <div
                data-testid="step-2-panel"
                className="border border-[#0A0A0A] bg-[#F4F3ED] p-7 lg:p-10"
              >
                <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
                  Step 2 · Pay ₹999
                </div>
                <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-2">
                  Two-tap payment.
                </h2>
                <p className="font-body text-sm text-[#4A4A4A] mb-6">
                  Tap the button to open Razorpay → enter the amount{" "}
                  <strong>₹999</strong> → pay via UPI / card / netbanking. Then
                  paste your <strong>Razorpay Payment ID</strong> (starts with{" "}
                  <code className="font-mono-acc">pay_…</code>) exactly as
                  shown on the success page. We verify the amount server-side
                  with Razorpay — underpayments are auto-rejected and cannot
                  unlock the group.
                </p>

                <div
                  data-testid="razorpay-pay-link"
                  className="mb-6 border border-[#0A0A0A] bg-white p-4"
                >
                  <RazorpayButton
                    buttonId="pl_TLbUKx5re6Zu7c"
                    amount={999}
                    packageLabel="₹999 · 10 trading days · Great Eagle Community"
                  />
                </div>

                <form onSubmit={submitStep2} className="space-y-4">
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Razorpay Payment ID *
                    </label>
                    <input
                      data-testid="payment-ref"
                      required
                      value={payRef}
                      onChange={(e) => setPayRef(e.target.value)}
                      placeholder="pay_O2xQqkL3z…"
                      className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none font-mono-acc"
                    />
                    <p className="font-mono-acc text-[10px] tracking-[0.15em] text-[#4A4A4A] mt-1.5">
                      Amount verified server-side · underpayments rejected · each pay_id unlocks once.
                    </p>
                  </div>
                  <button
                    data-testid="community-submit-2"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#D9381E] text-white py-4 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#0A0A0A] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3 border border-[#D9381E] hover:border-[#0A0A0A]"
                  >
                    {loading ? "Unlocking…" : "Unlock WhatsApp group"}
                    <LockKey weight="bold" size={16} />
                  </button>
                </form>

                <div className="mt-6 pt-6 border-t border-[#D4D0C5] flex items-start gap-2 text-[#4A4A4A]">
                  <ShieldCheck size={18} weight="duotone" className="text-[#D9381E] mt-0.5 shrink-0" />
                  <p className="font-body text-xs leading-relaxed">
                    Every payment ID can only ever unlock the group once. If you
                    reuse someone else's ID it will be rejected and you will be
                    removed without refund.
                  </p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div
                data-testid="step-3-panel"
                className="border border-[#0A0A0A] bg-[#F4F3ED] p-7 lg:p-10"
              >
                <CheckCircle size={56} weight="duotone" className="text-[#D9381E] mb-4" />
                <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
                  Step 3 · You're in.
                </div>
                <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-3">
                  Welcome, {form.name.split(" ")[0] || "trader"}.
                </h2>
                <p className="font-body text-[#4A4A4A] mb-6">
                  Tap below to join the WhatsApp group. Download the handbook to
                  prep before your first live session.
                </p>

                {whatsappLink ? (
                  <div className="border border-[#0A0A0A] bg-[#EAE8DF] p-5 mb-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
                        <span
                          className="font-mono-acc text-xs truncate"
                          data-testid="wa-link-display"
                        >
                          {whatsappLink}
                        </span>
                      </div>
                      <button
                        data-testid="wa-copy"
                        onClick={copyLink}
                        className="font-mono-acc text-[10px] tracking-[0.25em] uppercase border border-[#0A0A0A] px-2.5 py-1.5 hover:bg-[#0A0A0A] hover:text-[#F4F3ED] transition-colors"
                      >
                        <Copy size={12} weight="bold" className="inline mr-1" /> Copy
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="join-wa-btn"
                    className="flex-1 inline-flex items-center justify-center gap-3 bg-[#25D366] text-[#0A0A0A] py-4 font-body text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#1ebd5b] transition-colors"
                  >
                    <WhatsappLogo size={20} weight="fill" />
                    Join WhatsApp Group
                  </a>
                  <Link
                    to="/community/handbook"
                    target="_blank"
                    data-testid="download-handbook"
                    className="flex-1 inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-[#F4F3ED] py-4 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors"
                  >
                    <FilePdf size={18} weight="bold" />
                    Download Handbook
                  </Link>
                </div>

                <div className="mt-7 border-t border-[#D4D0C5] pt-5" data-testid="paid-expectations-block">
                  <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
                    How the next 10 trading days work
                  </div>
                  <ul className="space-y-2">
                    {[
                      "You'll receive the daily trade plan on WhatsApp before market open.",
                      "Nifty / Bank Nifty / Midcap futures levels + ranges will be posted through the day.",
                      "EOD sessions are live — join whenever your schedule allows.",
                      "We follow the plan. Very few trades on most days — that's Market Profile & Order Flow discipline.",
                    ].map((line, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-sm text-[#4A4A4A]">
                        <span className="text-[#D9381E] mt-0.5">→</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — what you get / locked summary */}
          <aside className="lg:col-span-5">
            <div className="border border-[#0A0A0A] bg-[#0A0A0A] text-[#F4F3ED] p-7 lg:p-9">
              <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
                INCLUDED · ₹999
              </div>
              <h3 className="font-display font-black text-2xl lg:text-3xl tracking-tighter mb-5">
                What you get the moment you pay.
              </h3>
              <ul className="space-y-3 mb-6">
                {[
                  "10 trading days of live support (₹99 / day)",
                  "Daily trade plan on WhatsApp",
                  "Nifty / Bank Nifty / Midcap futures levels",
                  "Intraday range updates on WhatsApp",
                  "Attend EOD discussion sessions live",
                  "Private WhatsApp group access",
                  "Full introduction to our trading ecosystem",
                ].map((t, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 font-body text-sm text-[#F4F3ED]"
                  >
                    <CheckCircle size={18} weight="fill" className="text-[#D9381E] shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              <div className="border-t border-[#2A2A2A] pt-5 mb-5" data-testid="community-expectations">
                <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#FFB703] mb-2">
                  Set the expectation
                </div>
                <p className="font-body text-[13px] leading-relaxed text-[#A1A1AA]">
                  This isn't a full-day market commentary group. We move
                  strictly as per our <strong className="text-[#F4F3ED]">trade
                  plan</strong>. Trades are <strong className="text-[#F4F3ED]">
                  few</strong> — that's how Market Profile & Order Flow work.
                  Quality over noise.
                </p>
              </div>

              <div className="border-t border-[#2A2A2A] pt-5">
                <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA] mb-2">
                  Step {step} of 3
                </div>
                {step < 3 ? (
                  <div className="flex items-center gap-2 text-[#A1A1AA]">
                    <Lock size={16} weight="duotone" />
                    <span className="font-body text-sm">
                      Group link is locked until payment is confirmed.
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[#25D366]">
                    <CheckCircle size={18} weight="fill" />
                    <span className="font-body text-sm font-semibold">
                      Group link unlocked.
                    </span>
                  </div>
                )}
              </div>
            </div>

            <p className="mt-4 font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]">
              Be civil in the group. Trades shared are for learning — not
              financial advice. Repeat offenders are removed without refund.
            </p>
          </aside>
        </div>
      </section>

      {/* NEW: Market Profile education + PDF card */}
      <MarketProfileEducation />

      {/* NEW: How to Get Started — blue 3-step block */}
      <HowToGetStarted onRegisterClick={scrollToForm} />

      {/* NEW: Instructions for New Members */}
      <InstructionsForNewMembers />

      {/* NEW: Daily Levels sample */}
      <DailyLevelsSample />

      {/* NEW: Community FAQ */}
      <CommunityFAQ />

      {/* NEW: Footer */}
      <CommunityFooter />
    </div>
  );
}
